import { Box } from "@material-ui/core";
import ibanLib from "iban";
import React, { useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { ProcessContext } from "../../../../contexts/ProcessContext";
import { fakeFormsDataIncome } from "../../../../utils/fakeFormsData";
import { useActionCallback } from "../../../../utils/hooks/useActionCallback";
import { useStepHandler } from "../../../../utils/hooks/useStepHandler";
import { DeclaredIncomeDataMapper } from "../../../../utils/mappers/DeclaredIncomeDataMapper";
import { PatchAddBorrowerCashInCashOutMethod } from "../../../../utils/mappers/PatchAddBorrowerCashInCashOutMethod";
import { PatchSetBorrowerCashInCashOutMethodToLoan } from "../../../../utils/mappers/PatchSetBorrowerCashInCashOutMethodToLoan";
import BackButton from "../../../shared/components/BackButton";
import { FLOW_MESSAGES } from "../../../shared/components/flowMessages/components";
import { STEP_GROUP_IDS } from "../../../shared/config";
import SetBankAccount from "../shared/steps/SetBankAccount";
import {
  DECLARED_INCOME,
  DOCUMENT_ID_TYPES,
  isSetBankAccountStepAllowedByRole,
  netIncomeTypesConfig,
} from "./config";
import EditIncomeForm from "./shared/steps/EditIncomeForm";

export default function DeclaredIncome(props) {
  const {
    state: { loan, participant },
    dispatch,
  } = useContext(ProcessContext);

  const {
    disableNetIncomeType,
    forceCaixaBankIBAN,
    disableOthers,
    allowInternationalBorrower,
    alternativePaymentMethod,
  } = props;

  const STEP_GROUP_STEPS_ARRAY = [
    {
      id: DECLARED_INCOME.EDIT_INCOME_FORM,
      show: () => true,
    },
    {
      id: DECLARED_INCOME.SET_BANK_ACCOUNT,
      show: () => isSetBankAccountStepAllowedByRole(participant),
    },
  ];
  const CURRENT_STEP_GROUP = STEP_GROUP_IDS.DeclaredIncome;

  const [state, setState] = useState({
    currentStepGroupStep: DECLARED_INCOME.EDIT_INCOME_FORM,
    values: {},
    formSubmitSuccess: false,
    bankAccountIBAN: loan.borrower_cash_in_cash_out_method_account_number,
    cashInCashOutMethodId: null,
    declaredIncomeId: null,
  });

  const { values, formSubmitSuccess, bankAccountIBAN } = state;

  const onSubmitModule = (values) => {
    const declaredIncomeId = uuidv4();
    const valuesWithDefaultNetIncomeType = {
      ...values,
      netIncomeType: netIncomeTypesConfig.SALARY.value,
    };
    const valuesToSend = disableNetIncomeType
      ? valuesWithDefaultNetIncomeType
      : values;

    setState((s) => ({
      ...s,
      declaredIncomeId: declaredIncomeId,
      bankAccountIBAN: values?.bankAccountIBAN
        ? ibanLib.electronicFormat(values.bankAccountIBAN)
        : undefined,
      values: valuesToSend,
    }));

    const dehydratedData = DeclaredIncomeDataMapper.hydrate({
      ...valuesToSend,
      id: declaredIncomeId,
      loanId: loan.id,
      borrowerId: participant.participantId,
    });

    dispatch({
      type: "REQUEST_POST_DECLARED_INCOME",
      payload: dehydratedData,
    });
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: {
        show: true,
      },
    });
  };

  const currentModuleStepHandler = useStepHandler(
    onSubmitModule,
    fakeFormsDataIncome
  );

  const { manageGroup, submitStep, onBack, backButtonVisible, getStepData } =
    currentModuleStepHandler;

  const stepGroupStepToShow = manageGroup(
    CURRENT_STEP_GROUP,
    STEP_GROUP_STEPS_ARRAY
  );

  const processPatchAddBorrowerCashInCashOutMethod = () => {
    const cashInCashOutMethodId = uuidv4();
    setState((s) => ({
      ...s,
      cashInCashOutMethodId: cashInCashOutMethodId,
    }));
    const allDataToPatch = PatchAddBorrowerCashInCashOutMethod.dehydrate({
      ...values,
      cashInCashOutMethodId: cashInCashOutMethodId,
      bankAccountIBAN: bankAccountIBAN,
      participant,
    });
    dispatch({
      type: "REQUEST_PATCH_ADD_BORROWER_CASH_IN_CASH_OUT_METHOD",
      payload: allDataToPatch,
    });
  };

  const processGetDeclaredIncome = () => {
    dispatch({
      type: "REQUEST_GET_DECLARED_INCOME",
      payload: {
        loanId: loan.id,
        onboardingDetailsId: participant.onboardingDetails.id,
      },
    });
  };
  const processPatchSetBorrowerCashInCashOutMethodToLoan = () => {
    const allDataToPatch = PatchSetBorrowerCashInCashOutMethodToLoan.dehydrate({
      id: loan.id,
      cashInCashOutMethodId: state.cashInCashOutMethodId,
    });
    dispatch({
      type: "REQUEST_PATCH_SET_BORROWER_CASH_IN_CASH_OUT_METHOD_TO_LOAN_AND_SEQUENCE",
      payload: allDataToPatch,
    });
  };

  const setItemStepOkAndSendOkMessage = () => {
    if (formSubmitSuccess) {
      dispatch({
        type: "SET_FLOW_ITEM_OK",
      });
      dispatch({
        type: "SET_FLOW_LOADING",
        payload: { show: false },
      });
      dispatch({
        type: "SET_FLOW_MESSAGE",
        payload: { id: FLOW_MESSAGES.SEND_OK.id },
      });
    }
  };

  useActionCallback("REQUEST_POST_DECLARED_INCOME_FULFILLED", () => {
    if (isSetBankAccountStepAllowedByRole(participant)) {
      setTimeout(() => processGetDeclaredIncome(), 2000);
      processPatchAddBorrowerCashInCashOutMethod();
    } else {
      setState((s) => ({
        ...s,
        formSubmitSuccess: true,
      }));
    }
  });

  useActionCallback(
    "REQUEST_PATCH_ADD_BORROWER_CASH_IN_CASH_OUT_METHOD_FULFILLED",
    () => {
      processPatchSetBorrowerCashInCashOutMethodToLoan();
    }
  );

  useActionCallback(
    "REQUEST_PATCH_ADD_BORROWER_CASH_IN_CASH_OUT_METHOD_REJECTED",
    () => {
      dispatch({
        type: "SET_FLOW_LOADING",
        payload: { show: false },
      });
    }
  );

  useActionCallback(
    "REQUEST_PATCH_SET_BORROWER_CASH_IN_CASH_OUT_METHOD_TO_LOAN_AND_SEQUENCE_FULFILLED",
    () => {
      setState((s) => ({
        ...s,
        formSubmitSuccess: true,
      }));
    }
  );

  useEffect(() => {
    if (formSubmitSuccess) {
      setItemStepOkAndSendOkMessage();
    }
  }, [formSubmitSuccess]);

  return (
    <>
      <Box className="appear-anim" style={{ position: "absolute" }}>
        <BackButton onBack={onBack} show={backButtonVisible} />
      </Box>
      {stepGroupStepToShow === DECLARED_INCOME.EDIT_INCOME_FORM && (
        <EditIncomeForm
          onSubmitForm={submitStep}
          data={getStepData()}
          disableNetIncomeType={disableNetIncomeType}
          disableOthers={disableOthers}
        />
      )}
      {stepGroupStepToShow === DECLARED_INCOME.SET_BANK_ACCOUNT && (
        <SetBankAccount
          onSubmitForm={submitStep}
          data={getStepData()}
          recordedIBAN={bankAccountIBAN}
          forceCaixaBankIBAN={forceCaixaBankIBAN}
          allowInternationalBorrower={
            allowInternationalBorrower &&
            participant.onboardingDetails?.user_details?.document_id
              ?.document_type !== DOCUMENT_ID_TYPES.NIF
          }
          alternativePaymentMethod={alternativePaymentMethod}
        />
      )}
    </>
  );
}
